import { useState } from "react";
import Modal from "../common/Modal";
import Loader from "../common/Loader.js";
import { useAuth } from "../../hooks/useAuth.js";
import Switch from "react-switch";
import { confirm } from "../common/ConfirmModal.js";
import api from "../../services/api.js";
import { useTranslation } from "react-i18next";

function ModalLocationSelect() {
  const { auth, changeLocation, setAuth, showModalChangeLocation, setShowModalChangeLocation, location } = useAuth();
  const [selectedLocationId, setSelectedLocationId] = useState(location);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberLocation, setRememberLocation] = useState(false);
  const { t } = useTranslation();

  // Two modes. The original one reconciles a device/account mismatch, where
  // "All locations" is a fine answer. The other is raised at login when a
  // multi-location user has no home location at all (useAuth.checkLocation):
  // there, "All locations" is exactly the answer that leaves every new encounter
  // without a location, so it is not offered and the modal cannot be dismissed
  // without a real choice.
  const requireLocation = showModalChangeLocation?.requireLocation === true;
  const canConfirm = !requireLocation || !!selectedLocationId;

  const confirmSelection = async () => {
    if (!canConfirm) return;
    setIsSubmitting(true);
    changeLocation(selectedLocationId);
    setShowModalChangeLocation(false);
    setIsSubmitting(false);

    if (rememberLocation) {
      confirm({
        message: t("locationSelect.preferenceStoredMessage"),
        title: t("locationSelect.preferenceStoredTitle"),
      });

      if (!auth.user.custom) {
        auth.user.custom = {};
      }
      auth.user.custom.doNotAskLocation = true;
      let res = await api.updateProfile({ custom: { doNotAskLocation: true } });

      if (res.status === 200 || res.status === 204) {
        setAuth({ ...auth });
      }
    }
  };

  return (
    <Modal
      show={showModalChangeLocation}
      close={() => {}}
      title={requireLocation ? t("locationSelect.requiredTitle") : t("locationSelect.title")}
      size="small"
      hideClose={true}
      action={
        <button onClick={confirmSelection} disabled={!canConfirm || isSubmitting}>
          {isSubmitting ? <Loader /> : t("locationSelect.continue")}
        </button>
      }
    >
      <div className="m-20">
        <p className="mb-10">{requireLocation ? t("locationSelect.requiredSubtitle") : t("locationSelect.subtitle")}</p>
        {!requireLocation && (
          <>
            <div className="mb-5">
              <small>
                <span className="width-180">{t("locationSelect.lastUsedDevice")}</span>
                <b>{showModalChangeLocation?.device || t("locations.allLocations")}</b>
              </small>
            </div>
            <div className="mb-20">
              <small>
                <span className="width-180">{t("locationSelect.lastUsedAccount")}</span>
                <b>{showModalChangeLocation?.account || t("locations.allLocations")}</b>
              </small>
            </div>
          </>
        )}
        <select
          className="modal-location-select"
          value={selectedLocationId}
          onChange={(e) => setSelectedLocationId(e.target.value)}
          disabled={isSubmitting}
          aria-label={t("locationSelect.ariaSelect")}
        >
          <option value="">{requireLocation ? t("locationSelect.choosePlaceholder") : t("locations.allLocations")}</option>
          {auth?.user?.locations?.map((loc) => (
            <option value={String(loc.id)} key={loc.id}>
              {loc.name}
            </option>
          ))}
        </select>

        {/* `doNotAskLocation` suppresses the device/account mismatch prompt. It has
            no meaning here: confirming this modal writes a home location, which is
            itself what stops the prompt from coming back. */}
        {!requireLocation && (
          <div className="swicth-row mt-40">
            <Switch
              checked={rememberLocation}
              onChange={(e) => setRememberLocation(e)}
              uncheckedIcon={false}
              checkedIcon={false}
              height={24}
              width={45}
            />
            {t("locationSelect.doNotAsk")}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ModalLocationSelect;
